
import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Building, MapPin, Home, BedDouble, Ruler, CreditCard, Calendar, FileText, Wallet, User, AtSign, Phone } from "lucide-react";
import { createInquiry } from "@/services/inquiry";
import { toast } from "@/hooks/use-toast";
import { normalizePhoneNumber, isValidPhoneNumber } from "@/utils/phone";
import { countryCodes } from "@/constants";


// Format price for display
const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        maximumFractionDigits: 0,
    }).format(price);
};

interface DownloadBrochureDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    property: any;
    initialTab?: "brochure" | "floorPlan";
}

export function DownloadBrochureDialog({
    open,
    onOpenChange,
    property,
    initialTab = "brochure"
}: DownloadBrochureDialogProps) {
    const [activeTab, setActiveTab] = useState<string>(initialTab);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        interest: "I am interested",
    });
    const [countryCode, setCountryCode] = useState<string>(countryCodes[0].value);

    React.useEffect(() => {
        if (open && initialTab) {
            setActiveTab(initialTab);
        }
    }, [open, initialTab]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (value: string) => {
        setFormData(prev => ({ ...prev, interest: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        // Phone validation & normalization
        const fullPhone = countryCode + formData.phone;
        const normalizedPhone = normalizePhoneNumber(fullPhone);
        if (!isValidPhoneNumber(normalizedPhone)) {
            toast({
                variant: "destructive",
                title: "Invalid Phone Number",
                description: "Please enter a valid phone number with country code (e.g., +971501234567)",
            });
            setIsLoading(false);
            return;
        }

        try {
            await createInquiry({
                name: formData.name,
                email: formData.email,
                phone: normalizedPhone,
                message: formData.interest,
                inquiryType: "property",
                propertyId: property.id || property._id
            });

            toast({
                title: "Success",
                description: "Your request has been submitted. The download will start shortly.",
            });

            // Trigger Download
            // Check for flattened structure first, then nested
            let downloadUrl = "";

            // Brochure URL logic
            if (activeTab === 'brochure') {
                downloadUrl = property.brochure || property.supportFile?.brochure;
            }
            // Floor Plan URL logic
            else {
                downloadUrl = property.floorPlan || property.supportFile?.floorPlan;
                // Note: In PropertyDetail, floorPlan might be an encoded S3 URL or /placeholder.svg
                // We should handle the case where it's a placeholder.
            }

            if (downloadUrl && !downloadUrl.includes('placeholder.svg')) {
                // If it's a full URL, open it. 
                // If it needs decoding, assume it's already decoded in the property object passed (PropertyDetail passes encodedS3Url result)
                window.open(downloadUrl, '_blank');
            } else {
                toast({
                    title: "Not Available",
                    description: "The requested file is not available for download.",
                    variant: "destructive"
                });
            }

            onOpenChange(false);
            setFormData({
                name: "",
                email: "",
                phone: "",
                interest: "I am interested",
            });
            setCountryCode(countryCodes[0].value);
        } catch (error: any) {
            console.error("Submission error:", error);
            toast({
                title: "Error",
                description: "Failed to submit request. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (!property) return null;

    // Helper to safely get fields
    const getField = (key: string, nestedKey?: string) => {
        if (property[key] !== undefined && property[key] !== null) return property[key];
        if (nestedKey) {
            const parts = nestedKey.split('.');
            let current = property;
            for (const part of parts) {
                if (current?.[part] !== undefined) current = current[part];
                else return undefined;
            }
            return current;
        }
        return undefined;
    };

    const price = getField('price') || getField('pricing', 'amount') || 0;
    const developer = getField('developer') || getField('management', 'developer') || "Unknown";
    const location = getField('location') || getField('location', 'locality') || "-";
    const propertyType = getField('propertyType') || getField('specifications', 'propertyType') || "-";
    const beds = getField('bedrooms') || getField('specifications', 'beds') || "-";
    const area = getField('area') || getField('specifications', 'size') || "-";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl p-0 overflow-hidden gap-0 border-none sm:rounded-2xl">
                <div className="grid grid-cols-1 md:grid-cols-12 h-full">
                    {/* Left Side: Property Summary */}
                    <div className="md:col-span-5 bg-muted/30 p-6 md:p-8 flex flex-col justify-center border-r">
                        <h2 className="text-xl md:text-2xl font-bold mb-6 leading-tight">{property.title}</h2>

                        <div className="mb-8 bg-primary/10 p-4 rounded-xl border border-primary/20">
                            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">STARTING FROM</span>
                            <span className="text-2xl font-bold text-foreground">
                                {formatPrice(price)}<span className="text-sm font-normal text-muted-foreground ml-1">AED</span>
                            </span>
                        </div>

                        <div className="space-y-5">
                            <div className="flex items-center gap-3">
                                <Building className="h-5 w-5 text-primary" />
                                <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                    <span className="text-sm text-muted-foreground">Developer:</span>
                                    <span className="font-medium text-foreground">{developer}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <MapPin className="h-5 w-5 text-primary" />
                                <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                    <span className="text-sm text-muted-foreground">Community:</span>
                                    <span className="font-medium text-foreground text-right">{location}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Home className="h-5 w-5 text-primary" />
                                <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                    <span className="text-sm text-muted-foreground">Property Type:</span>
                                    <span className="font-medium text-foreground">{propertyType}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <BedDouble className="h-5 w-5 text-primary" />
                                <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                    <span className="text-sm text-muted-foreground">Accommodation:</span>
                                    <span className="font-medium text-foreground">{beds} Bedrooms</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Ruler className="h-5 w-5 text-primary" />
                                <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                    <span className="text-sm text-muted-foreground">Size:</span>
                                    <span className="font-medium text-foreground">{area} Sq Ft.</span>
                                </div>
                            </div>

                            {property.paymentPlan && (
                                <>
                                    <div className="flex items-center gap-3">
                                        <Wallet className="h-5 w-5 text-primary" />
                                        <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                            <span className="text-sm text-muted-foreground">Down Payment:</span>
                                            <span className="font-medium text-foreground">{property.paymentPlan.onBooking ? `${property.paymentPlan.onBooking}` : "-"}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CreditCard className="h-5 w-5 text-primary" />
                                        <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                            <span className="text-sm text-muted-foreground">Payment Plan:</span>
                                            <span className="font-medium text-foreground">{property.paymentPlan.planType || "-"}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Calendar className="h-5 w-5 text-primary" />
                                        <div className="flex-1 flex justify-between items-center border-b border-border pb-2 border-dashed">
                                            <span className="text-sm text-muted-foreground">Handover:</span>
                                            <span className="font-medium text-foreground">{property.paymentPlan.onHandover || "-"}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Right Side: Form */}
                    <div className="md:col-span-7 p-6 md:p-8 flex flex-col">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold flex items-center text-foreground">
                                <span className="mr-2 text-primary">
                                    {activeTab === 'brochure' ? <FileText className="h-6 w-6" /> : <Ruler className="h-6 w-6" />}
                                </span>
                                {activeTab === 'brochure' ? 'Download Brochure' : 'Download Floor Plan'}
                            </h2>
                        </div>

                        <Tabs value={activeTab} onValueChange={(val: any) => setActiveTab(val)} className="w-full mb-6">
                            <TabsList className="grid w-full grid-cols-2 p-1 bg-muted rounded-lg">
                                <TabsTrigger value="brochure" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2.5 transition-all">
                                    Project Brochure
                                </TabsTrigger>
                                <TabsTrigger value="floorPlan" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2.5 transition-all">
                                    Floor Plan
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <form onSubmit={handleSubmit} className="space-y-5 flex-1 flex flex-col justify-center">
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Name</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Name *"
                                        required
                                        className="pl-10"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Email ID</label>
                                <div className="relative">
                                    <AtSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Email Id *"
                                        required
                                        className="pl-10"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Contact No</label>
                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        <Select value={countryCode} onValueChange={setCountryCode}>
                                            <SelectTrigger className="w-[110px]">
                                                <SelectValue placeholder="Code" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {countryCodes.map((code) => (
                                                    <SelectItem key={code.label} value={code.value}>
                                                        {code.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <div className="relative flex-1">
                                            <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="+971501234567"
                                                className="pl-10"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Select value={formData.interest} onValueChange={handleSelectChange}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="I am interested" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="I am interested">I am interested</SelectItem>
                                        <SelectItem value="I am an investor">I am an investor</SelectItem>
                                        <SelectItem value="I am an end user">I am an end user</SelectItem>
                                        <SelectItem value="I am a real estate agent">I am a real estate agent</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 uppercase mt-4 text-base shadow-lg transition-all hover:translate-y-[-2px]" disabled={isLoading}>
                                {isLoading ? "Processing..." : "DOWNLOAD NOW"}
                            </Button>
                        </form>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
